import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  SwipeableDrawer,
  Dialog,
  Divider,
  Button,
  Stack,
  Snackbar,
  Alert,
  useMediaQuery,
} from '@mui/material';
import {
  Reply as ReplyIcon,
  MoreHoriz,
  EditOutlined,
  DeleteOutline,
  ContentCopy,
  Link as LinkIcon,
  Close as CloseIcon,
  FlagOutlined,
} from '@mui/icons-material';
import { useAuth } from 'AuthContext';
import ProfileAvatar from 'components/ProfileAvatar';
import IOSConfirmDialog from 'components/IOSConfirmDialog';
import ReportContentDialog from 'components/ReportContentDialog';
import ReplyQuote from 'components/ReplyQuote';
import EmojiInput from 'components/EmojiInput';
import useFeedContext from 'contexts/Feed';
import useClubContext from 'contexts/Club';
import { reportContent } from 'services/moderation/moderation.service';
import { setOpenLibraryCoverSize, OL_COVER_SIZE } from 'services/openLibraryService';
import { bookCoverImgSx } from 'utils/bookCoverDisplay';
import { triggerHaptic } from 'utils/haptics';
import { formatSemanticDateTime } from 'utils/dateHelpers';
import { formatMeetingDisplay } from 'utils/meetingTime';
import { isIosNativeApp, iosBottomSheetPaperSx, iosSheetGrabberSx } from 'utils/iosNativeUi';
import { getPlatform } from 'utils/platformHelpers';
import { renderMentions, decodeMentionsForEdit, parseMentions, extractFirstLink } from 'utils/mentionHelpers';
import { aprilFoolsGoalTitle } from 'utils/aprilFools2026';
import QuarterGoalsReportActivity from 'components/QuarterGoalsReportActivity';
import {
  isQuarterGoalsReportActivity,
  parseQuarterGoalsReportPayload,
} from 'utils/quarterGoalsReportActivity';

const PostCard = ({ post, isFirstInGroup = true }) => {
  const { user } = useAuth();
  const { currentClub } = useClubContext();
  // iOS/Safari synthesizes mouseenter before click on tap; hover+toggle cancels itself.
  const isTouchPrimary =
    useMediaQuery('(hover: none) and (pointer: coarse)', { noSsr: true }) ||
    isIosNativeApp();
  const {
    registerPostRef,
    addReaction,
    deletePost,
    setReplyingTo,
    setEditingPost,
    blockUser,
  } = useFeedContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [imageViewer, setImageViewer] = useState(null);
  const [viewerSlideIndex, setViewerSlideIndex] = useState(0);
  const imageViewerScrollRef = useRef(null);
  const [copySnackbar, setCopySnackbar] = useState({ open: false, message: '' });
  const postRef = useRef(null);
  const emojiInputRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const longPressHandledRef = useRef(false);
  const pressMovedRef = useRef(false);
  const pendingOpenPickerRef = useRef(false);

  useEffect(() => {
    if (postRef.current) {
      registerPostRef(post.id, postRef.current);
    }
    return () => {
      registerPostRef(post.id, null);
    };
  }, [post.id, registerPostRef]);

  useEffect(() => () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
    }
  }, []);

  useEffect(() => {
    if (!isTouchPrimary) return undefined;

    const hideIfOutside = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (postRef.current?.contains(target)) return;
      if (target.closest('.MuiPopover-root, .MuiModal-root, .MuiDrawer-root')) return;
      setShowReactions(false);
    };

    document.addEventListener('pointerdown', hideIfOutside);
    return () => document.removeEventListener('pointerdown', hideIfOutside);
  }, [isTouchPrimary]);

  const authorName = post.authorName || post.author?.displayName || 'Unknown';
  const authorId = post.authorId || post.author?.uid;
  const isActivity = !!post.isActivity;
  const isAuthor = user && (post.authorId === user.uid || post.author?.uid === user.uid);
  const canModerate = currentClub && ['owner', 'admin'].includes(currentClub.role);
  const canDelete = isAuthor || canModerate;
  const canReportOrBlock = user && authorId && !isAuthor && !isActivity;
  const showAvatar = isFirstInGroup && !isActivity;
  const { displayString: timestampLabel } = formatSemanticDateTime(post.created_at);
  const quickEmojis =
    (post.reactions || [])
      .map(r => r.emoji)
      .filter((emoji, index, arr) => arr.indexOf(emoji) === index)
      .slice(0, 6) || [];
  const quickEmojiChoices = quickEmojis.length > 0 ? quickEmojis : ['👍', '❤️', '😂', '🎉', '👏', '🔥'];

  const handleStartReply = () => {
    const { displayText } = parseMentions(post.text || '');
    setReplyingTo({
      id: post.id,
      authorName,
      text: displayText,
      isSpoiler: !!post.isSpoiler,
      hasImages: Array.isArray(post.images) && post.images.length > 0,
    });
  };

  const openActions = () => {
    triggerHaptic('medium');
    setActionsOpen(true);
  };

  const closeActions = () => {
    setActionsOpen(false);
  };

  const openFullPicker = () => {
    triggerHaptic('light');
    // Reuse the same EmojiInput popover that works for the + reaction button.
    pendingOpenPickerRef.current = true;
    setShowReactions(true);
    if (!actionsOpen) {
      pendingOpenPickerRef.current = false;
      emojiInputRef.current?.openPicker();
      return;
    }
    setActionsOpen(false);
    // Fallback if TransitionProps.onExited does not fire on some WebViews.
    window.setTimeout(() => {
      if (!pendingOpenPickerRef.current) return;
      pendingOpenPickerRef.current = false;
      emojiInputRef.current?.openPicker();
    }, 400);
  };

  const handleActionsDrawerExited = () => {
    if (!pendingOpenPickerRef.current) return;
    pendingOpenPickerRef.current = false;
    emojiInputRef.current?.openPicker();
  };

  const handleStartEdit = () => {
    const { displayText, mentions } = decodeMentionsForEdit(post.text || '');
    setEditingPost({
      id: post.id,
      text: displayText,
      mentions,
      isSpoiler: !!post.isSpoiler,
      previewText: displayText,
      hasImages: Array.isArray(post.images) && post.images.length > 0,
    });
    closeActions();
  };

  const handleCopyText = async () => {
    const text = post?.text;
    if (!text) return;
    const { displayText } = parseMentions(text);
    try {
      await navigator.clipboard.writeText(displayText);
      triggerHaptic('light');
      setCopySnackbar({ open: true, message: 'Copied to clipboard' });
      closeActions();
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handleCopyLink = async () => {
    const link = extractFirstLink(post?.text);
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      triggerHaptic('light');
      setCopySnackbar({ open: true, message: 'Link copied' });
      closeActions();
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const firstLink = extractFirstLink(post?.text);

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handlePointerDown = (e) => {
    if (e.pointerType !== 'touch') return;
    if (e.target.closest('[data-emoji-reaction]')) return;
    if (e.target.closest('[data-post-image]')) return;
    pressMovedRef.current = false;
    longPressHandledRef.current = false;
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      longPressHandledRef.current = true;
      openActions();
    }, 600);
  };

  const handlePointerMove = (e) => {
    if (e.pointerType !== 'touch') return;
    if (!longPressTimerRef.current) return;
    // Cancel if user starts scrolling/dragging
    if (Math.abs(e.movementX) > 6 || Math.abs(e.movementY) > 6) {
      pressMovedRef.current = true;
      cancelLongPress();
    }
  };

  const handlePointerUp = (e) => {
    if (e.pointerType !== 'touch') return;
    cancelLongPress();
    setTimeout(() => {
      longPressHandledRef.current = false;
    }, 50);
  };

  const handlePointerCancel = () => {
    cancelLongPress();
    longPressHandledRef.current = false;
  };

  const handlePostClick = (e) => {
    if (longPressHandledRef.current) {
      return;
    }
    if (e.target.closest('[data-post-image]')) {
      return;
    }
    // Touch: tap the message to show/hide the add-reaction button.
    // Hover devices use mouseenter/leave instead — a tap also fires those,
    // so toggling here would hide the button on the first click.
    if (isTouchPrimary && !e.target.closest('[data-emoji-reaction]')) {
      setShowReactions((prev) => !prev);
    }
  };

  const handleImagePointerEvent = (e) => {
    e.stopPropagation();
  };

  const handleQuickEmoji = async (emoji) => {
    try {
      await addReaction(post.id, emoji);
      triggerHaptic('light');
      setActionsOpen(false);
    } catch (err) {
      console.error('Error adding reaction from sheet:', err);
    }
  };

  const postImages = Array.isArray(post.images) ? post.images.filter(Boolean) : [];
  const showImages = postImages.length > 0 && (!post.isSpoiler || isRevealed);

  const openImageGallery = useCallback(
    (e, startIndex) => {
      e.stopPropagation();
      if (!postImages.length) return;
      const safeIndex = Math.min(Math.max(0, startIndex), postImages.length - 1);
      setViewerSlideIndex(safeIndex);
      setImageViewer({ urls: postImages, index: safeIndex });
    },
    [postImages],
  );

  const closeImageGallery = useCallback(() => {
    setImageViewer(null);
  }, []);

  useLayoutEffect(() => {
    if (!imageViewer || !imageViewerScrollRef.current) return undefined;
    const el = imageViewerScrollRef.current;
    const applyScroll = () => {
      const w = el.clientWidth;
      if (w > 0) {
        el.scrollLeft = imageViewer.index * w;
        setViewerSlideIndex(imageViewer.index);
      }
    };
    applyScroll();
    const ro = new ResizeObserver(applyScroll);
    ro.observe(el);
    return () => ro.disconnect();
  }, [imageViewer]);

  const handleViewerScroll = useCallback((e) => {
    const el = e.currentTarget;
    const w = el.clientWidth;
    if (w <= 0) return;
    setViewerSlideIndex(Math.round(el.scrollLeft / w));
  }, []);

  const relatedRecord = post.relatedRecord || null;
  const isMeetingActivity = post.text?.includes('{meeting_post}') && relatedRecord?.type === 'meeting';
  const isBookCompletionActivity = post.text?.includes('{book_completion_post}') && relatedRecord?.type === 'book';
  const isGoalCompletionActivity = post.text?.includes('{goal_completion_post}') && relatedRecord?.type === 'goal';
  const isMemberJoinActivity = post.text?.includes('{member_join_post}');
  const isQuarterGoalsReport = isQuarterGoalsReportActivity(post.text);
  const quarterGoalsReportPayload = isQuarterGoalsReport
    ? parseQuarterGoalsReportPayload(post.text)
    : null;
  const streakMatch = post.text?.match(/\|streak:([^|]+)$/);
  const goalStreakValue = streakMatch?.[1]?.trim() || null;

  const renderMeetingActivity = () => {
    const meetingData = relatedRecord?.record || {};

    const meetingTitle = meetingData.title || 'Book Club Meeting';
    const location = meetingData.location;
    const details = meetingData.notes;
    const meetingBook = meetingData.book;
    const meetingTheme = meetingData.theme;
    const display = formatMeetingDisplay({
      date: meetingData.date,
      startTime: meetingData.startTime,
      timezone: meetingData.timezone,
    });
    const actionLabel = 'Meeting';
    const showViewerTime =
      display.viewerTime &&
      (display.viewerDate !== display.hostDate || display.viewerTime !== display.hostTime);

    return (
      <Box
        sx={{
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: 1.5,
          mb: 0.5,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
          {actionLabel}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.25 }}>
          {meetingTitle}
        </Typography>
        {(display.hostDate || display.hostTime) && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.25 }}>
            📅 {display.hostDate}{display.hostTime ? ` · ${display.hostTime}` : ''}{display.hostLabel ? ` (${display.hostLabel})` : ''}
          </Typography>
        )}
        {showViewerTime && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.25 }}>
            Your time: {display.viewerDate} · {display.viewerTime}
          </Typography>
        )}
        {location && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.25 }}>
            📍 {location}
          </Typography>
        )}
        {meetingBook && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.25 }}>
            📚 {meetingBook.title}
            {meetingBook.author ? ` by ${meetingBook.author}` : ''}
          </Typography>
        )}
        {meetingTheme && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.25 }}>
            🎯 Theme: {meetingTheme}
          </Typography>
        )}
        {details && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
            {details}
          </Typography>
        )}
      </Box>
    );
  };

  const renderBookCompletionActivity = () => {
    const bookData = relatedRecord?.record || {};
    const actorName = post.authorName || 'A reader';

    const title = bookData.title || 'a book';
    const author = bookData.author;
    const coverImage = bookData.coverImage;

    return (
      <Box
        sx={{
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: 1.5,
          mb: 0.5,
          display: 'flex',
          gap: 1.25,
        }}
      >
        {coverImage && (
          <Box
            component="img"
            src={setOpenLibraryCoverSize(coverImage, OL_COVER_SIZE.M)}
            alt={title}
            sx={{
              ...bookCoverImgSx({
                width: 56,
                height: 80,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                flexShrink: 0,
              }),
            }}
          />
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
            {actorName} finished a book
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {title}
            {author ? ` by ${author}` : ''}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderMemberJoinActivity = () => {
    const memberName = post.authorName || 'A new member';

    return (
      <Box
        sx={{
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: 1.5,
          mb: 0.5,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
          New member
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {memberName} has joined the group
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
          Welcome them to the conversation.
        </Typography>
      </Box>
    );
  };

  const renderGoalCompletionActivity = () => {
    const goalData = relatedRecord?.record || {};
    const actorName = post.authorName || 'A member';
    const goalTitle = aprilFoolsGoalTitle(goalData.title || 'a goal', goalData.id);
    const goalType = goalData.type ? goalData.type.replace('_', ' ') : 'goal';
    const cadenceLabel = goalData.cadence ? `${goalData.cadence} ${goalType}` : goalType;
    let targetLabel = null;
    if (goalData.type === 'habit' && goalData.targetCount) {
      targetLabel = `${goalData.targetCount} times`;
    } else if (goalData.type === 'metric' && goalData.targetQuantity) {
      targetLabel = `${goalData.targetQuantity} ${goalData.unit || ''}`.trim();
    }

    return (
      <Box
        sx={{
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: 1.5,
          mb: 0.5,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
          🎉 {actorName} completed a goal
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {goalTitle}
        </Typography>
        {cadenceLabel && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {cadenceLabel}
            {targetLabel ? ` · ${targetLabel}` : ''}
          </Typography>
        )}
        {goalStreakValue && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            🔥 Streak: {goalStreakValue}
          </Typography>
        )}
        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={(event) => {
              event.stopPropagation();
              handleQuickEmoji('🎉');
            }}
            sx={{ textTransform: 'none' }}
          >
            Celebrate
          </Button>
        </Box>
      </Box>
    );
  };

  const handleDeleteConfirm = async () => {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      await deletePost(post.id);
      setDeleteConfirmOpen(false);
      closeActions();
    } catch (err) {
      console.error('Error deleting post:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReportAndBlockSubmit = async ({ reason, details }) => {
    if (!user || !authorId) return;
    setReportSubmitting(true);
    try {
      await reportContent({
        userId: user.uid,
        targetType: 'post',
        targetPostId: post.id,
        targetUserId: authorId,
        clubId: currentClub?.id,
        reason,
        details,
      });
      await blockUser(authorId);
      setReportOpen(false);
      setCopySnackbar({
        open: true,
        message: 'Report submitted. Their posts are hidden from your feed.',
      });
    } finally {
      setReportSubmitting(false);
    }
  };

  return (
    <Box
      ref={postRef}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onPointerMove={handlePointerMove}
      onMouseEnter={() => {
        if (isTouchPrimary) return;
        setShowReactions(true);
      }}
      onMouseLeave={() => {
        if (isTouchPrimary) return;
        setShowReactions(false);
      }}
      onClick={handlePostClick}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        px: 2,
        py: 0.75,
        backgroundColor: isActivity ? 'action.selected' : 'transparent',
        borderRadius: 2,
        border: isActivity ? '1px solid' : 'none',
        borderColor: isActivity ? 'divider' : 'transparent',
        '&:hover': {
          backgroundColor: isActivity ? 'action.selected' : 'action.hover',
        },
        transition: 'background-color 0.2s',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
        touchAction: 'manipulation',
      }}
    >
      {/* Avatar - only show for first message in group; pt aligns top of image with top of name text */}
      <Box sx={{ flexShrink: 0, width: 40, display: 'flex', justifyContent: 'center', pt: 0.35 }}>
        {showAvatar ? (
          <ProfileAvatar
            user={post.author}
            size={40}
            alt={authorName}
          />
        ) : (
          <Box sx={{ width: 40 }} />
        )}
      </Box>

      {/* Message Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Header with name and timestamp - only show for first in group */}
        {isActivity ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box
              sx={{
                backgroundColor: 'primary.main',
                px: 1,
                py: 0.25,
                borderRadius: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: 'primary.contrastText',
                  fontSize: '0.6875rem',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                Activity
              </Typography>
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.75rem',
              }}
            >
              {timestampLabel}
            </Typography>
          </Box>
        ) : showAvatar && (
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                fontSize: '0.9375rem',
                color: 'text.primary',
              }}
            >
              {authorName}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.75rem',
              }}
            >
              {timestampLabel}
            </Typography>
          </Box>
        )}

        {/* Reply Quote */}
        {post.parentPostId && post.parentPost && (
          <Box sx={{ mb: 0.75 }}>
            <ReplyQuote
              parentPostText={post.parentPost.text}
              parentPostId={post.parentPostId}
              parentAuthorName={post.parentPost.authorName || post.parentPost.author?.displayName || 'Unknown'}
              parentIsSpoiler={post.parentIsSpoiler}
            />
          </Box>
        )}

        {/* Message Text */}
        {isMeetingActivity ? (
          renderMeetingActivity()
        ) : isBookCompletionActivity ? (
          renderBookCompletionActivity()
        ) : isGoalCompletionActivity ? (
          renderGoalCompletionActivity()
        ) : isMemberJoinActivity ? (
          renderMemberJoinActivity()
        ) : isQuarterGoalsReport ? (
          <QuarterGoalsReportActivity payload={quarterGoalsReportPayload} />
        ) : post.isSpoiler && !isRevealed ? (
          <Box
            onClick={() => {
              setIsFadingOut(true);
              setTimeout(() => {
                setIsRevealed(true);
                setIsFadingOut(false);
              }, 300);
            }}
            sx={{
              cursor: 'pointer',
              mb: 0.5,
              userSelect: 'none',
              backgroundColor: 'action.hover',
              borderRadius: 1.5,
              px: 2,
              py: 2.5,
              border: '1px solid',
              borderColor: 'divider',
              transition: 'all 0.3s ease',
              opacity: isFadingOut ? 0 : 1,
              transform: isFadingOut ? 'scale(0.98)' : 'scale(1)',
              pointerEvents: isFadingOut ? 'none' : 'auto',
              '&:hover': {
                backgroundColor: 'action.selected',
                borderColor: 'primary.main',
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                textAlign: 'center',
                mb: 0.5,
              }}
            >
              Spoiler
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                textAlign: 'center',
                display: 'block',
                fontSize: '0.75rem',
              }}
            >
              Click to reveal
            </Typography>
          </Box>
        ) : (
          <Typography
            variant="body1"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontSize: '0.9375rem',
              lineHeight: 1.4,
              color: 'text.primary',
              fontWeight: isActivity ? 600 : 400,
              mb: 0.5,
            }}
          >
            {renderMentions(post.text)}
          </Typography>
        )}

        {showImages && (
          <Box sx={{ mt: 0.75 }}>
            <Box
              sx={{
                display: 'grid',
                gap: 1,
                gridTemplateColumns: postImages.length === 1 ? '1fr' : 'repeat(2, minmax(0, 1fr))',
              }}
            >
              {postImages.map((url, index) => (
                <Box
                  key={`${url}-${index}`}
                  component="img"
                  src={url}
                  alt={`Post image ${index + 1}`}
                  onClick={(e) => openImageGallery(e, index)}
                  onPointerDown={handleImagePointerEvent}
                  onPointerMove={handleImagePointerEvent}
                  onPointerUp={handleImagePointerEvent}
                  onPointerCancel={handleImagePointerEvent}
                  data-post-image
                  sx={{
                    width: '100%',
                    height: '100%',
                    maxHeight: postImages.length === 1 ? 340 : 200,
                    aspectRatio: postImages.length === 1 ? '4 / 3' : '1 / 1',
                    objectFit: 'cover',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.default',
                    cursor: 'zoom-in',
                    userSelect: 'auto',
                    WebkitUserSelect: 'auto',
                    WebkitTouchCallout: 'default',
                    touchAction: 'auto',
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Reactions - always show existing reactions, only show + button on hover/tap */}
        <Box 
          sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}
          onClick={(e) => e.stopPropagation()}
          data-emoji-reaction
        >
          <EmojiInput
            ref={emojiInputRef}
            postId={post.id}
            reactions={post.reactions || []}
            showAddButton={showReactions}
          />
          {showReactions && (
            <>
              <Tooltip title="Reply">
                <IconButton
                  size="small"
                  onClick={handleStartReply}
                  sx={{
                    opacity: 0.7,
                    '&:hover': { opacity: 1 },
                    padding: '4px',
                  }}
                >
                  <ReplyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              {(isAuthor || canReportOrBlock || canModerate) && (
                <Tooltip title="More actions">
                  <IconButton
                    size="small"
                    onClick={openActions}
                    sx={{
                      opacity: 0.7,
                      '&:hover': { opacity: 1 },
                      padding: '4px',
                    }}
                  >
                    <MoreHoriz sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </Box>

      </Box>
      <SwipeableDrawer
        anchor="bottom"
        open={actionsOpen}
        onClose={closeActions}
        onOpen={() => {}}
        // Android defaults enable edge swipe-to-open; with a no-op onOpen that can
        // peek/stick this sheet above BottomNav (worse on tall PWAs). Open via long-press only.
        disableSwipeToOpen
        disableDiscovery
        TransitionProps={{ onExited: handleActionsDrawerExited }}
        PaperProps={{
          sx: (theme) => ({
            maxHeight: '85vh',
            overflow: 'auto',
            touchAction: 'manipulation',
            pb: 2,
            ...(isIosNativeApp()
              ? iosBottomSheetPaperSx(theme)
              : { borderTopLeftRadius: 12, borderTopRightRadius: 12 }),
          }),
        }}
      >
        <Box sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="center" sx={{ mb: 1 }}>
            <Box
              sx={
                isIosNativeApp()
                  ? iosSheetGrabberSx
                  : { width: 40, height: 4, backgroundColor: 'divider', borderRadius: 2 }
              }
            />
          </Stack>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Quick reactions
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
            {quickEmojiChoices.map((emoji) => (
              <Button
                key={emoji}
                variant="outlined"
                onClick={() => handleQuickEmoji(emoji)}
                sx={{ minWidth: 48, minHeight: 40, fontSize: '1.2rem' }}
              >
                {emoji}
              </Button>
            ))}
          </Stack>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={openFullPicker}
            sx={{ textTransform: 'none', mb: 1 }}
          >
            Choose from full list
          </Button>
          <Divider sx={{ my: 1 }} />
          <Button
            fullWidth
            startIcon={<ReplyIcon />}
            onClick={() => {
              handleStartReply();
              closeActions();
            }}
            sx={{ textTransform: 'none' }}
          >
            Reply to {authorName}
          </Button>
          {post?.text?.trim() && (
            <Button
              fullWidth
              startIcon={<ContentCopy />}
              onClick={handleCopyText}
              sx={{ textTransform: 'none' }}
            >
              Copy post text
            </Button>
          )}
          {firstLink && (
            <Button
              fullWidth
              startIcon={<LinkIcon />}
              onClick={handleCopyLink}
              sx={{ textTransform: 'none' }}
            >
              Copy link
            </Button>
          )}
          {canDelete && (
            <>
              <Divider sx={{ my: 1 }} />
              {isAuthor && !isActivity && (
                <Button
                  fullWidth
                  startIcon={<EditOutlined />}
                  onClick={handleStartEdit}
                  sx={{ textTransform: 'none' }}
                >
                  Edit post
                </Button>
              )}
              <Button
                fullWidth
                color="error"
                startIcon={<DeleteOutline />}
                onClick={() => {
                  setDeleteConfirmOpen(true);
                  closeActions();
                }}
                sx={{ textTransform: 'none' }}
              >
                {canModerate && !isAuthor ? 'Delete (as admin)' : 'Delete post'}
              </Button>
            </>
          )}
          {canReportOrBlock && (
            <>
              <Divider sx={{ my: 1 }} />
              <Button
                fullWidth
                color="error"
                startIcon={<FlagOutlined />}
                onClick={() => {
                  setReportOpen(true);
                  closeActions();
                }}
                sx={{ textTransform: 'none' }}
              >
                Report & block
              </Button>
            </>
          )}
        </Box>
      </SwipeableDrawer>
      <IOSConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title="Delete post"
        description="This will permanently remove the post from the feed."
        cancelLabel="Cancel"
        confirmLabel="Delete"
        destructive
        confirmDisabled={isSubmitting}
        onConfirm={handleDeleteConfirm}
      />
      <ReportContentDialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        onSubmit={handleReportAndBlockSubmit}
        title="Report & block"
        submitting={reportSubmitting}
      />
      <Dialog
        open={Boolean(imageViewer)}
        onClose={closeImageGallery}
        fullScreen
        aria-label="Fullscreen images"
        slotProps={{
          container: {
            sx: {
              p: 0,
              alignItems: 'stretch',
            },
          },
          backdrop: {
            sx: {
              backgroundColor: 'common.black',
              backdropFilter: 'none',
              WebkitBackdropFilter: 'none',
            },
          },
          paper: {
            elevation: 0,
            sx: {
              m: 0,
              width: '100%',
              maxWidth: '100%',
              height: '100%',
              maxHeight: '100%',
              borderRadius: 0,
              border: 'none',
              boxShadow: 'none',
              bgcolor: 'common.black',
              backgroundImage: 'none',
              backdropFilter: 'none',
              WebkitBackdropFilter: 'none',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden',
            },
          },
        }}
      >
        {imageViewer && (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'common.black',
            }}
          >
            <IconButton
              type="button"
              onClick={closeImageGallery}
              aria-label="Close image viewer"
              sx={{
                position: 'fixed',
                top: (theme) => `calc(${theme.spacing(1)} + env(safe-area-inset-top, 0px))`,
                right: (theme) => `calc(${theme.spacing(1)} + env(safe-area-inset-right, 0px))`,
                zIndex: (theme) => theme.zIndex.modal + 1,
                color: 'common.white',
                backgroundColor: 'rgba(0,0,0,0.45)',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.6)' },
              }}
            >
              <CloseIcon />
            </IconButton>
            {imageViewer.urls.length > 1 && (
              <Typography
                component="div"
                role="status"
                aria-live="polite"
                sx={{
                  position: 'fixed',
                  bottom: (theme) => `calc(${theme.spacing(2)} + env(safe-area-inset-bottom, 0px))`,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: (theme) => theme.zIndex.modal + 1,
                  color: 'common.white',
                  typography: 'body2',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 2,
                  backgroundColor: 'rgba(0,0,0,0.45)',
                }}
              >
                {viewerSlideIndex + 1} / {imageViewer.urls.length}
              </Typography>
            )}
            <Box
              ref={imageViewerScrollRef}
              onScroll={handleViewerScroll}
              sx={{
                width: '100%',
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'stretch',
                overflowX: 'auto',
                overflowY: 'hidden',
                scrollSnapType: 'x mandatory',
                WebkitOverflowScrolling: 'touch',
                touchAction: 'pan-x',
                overscrollBehaviorX: 'contain',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {imageViewer.urls.map((url, slideIdx) => (
                <Box
                  key={`${url}-${slideIdx}`}
                  sx={{
                    flex: '0 0 100%',
                    width: '100%',
                    minWidth: '100%',
                    maxWidth: '100%',
                    height: '100%',
                    minHeight: '100%',
                    alignSelf: 'stretch',
                    scrollSnapAlign: 'start',
                    scrollSnapStop: 'always',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxSizing: 'border-box',
                    p: 2,
                  }}
                  onClick={(event) => {
                    if (event.target === event.currentTarget) {
                      closeImageGallery();
                    }
                  }}
                >
                  <Box
                    component="img"
                    src={url}
                    alt={`Post image ${slideIdx + 1} of ${imageViewer.urls.length}`}
                    onClick={(event) => event.stopPropagation()}
                    sx={{
                      display: 'block',
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      objectPosition: 'center',
                      flexShrink: 0,
                      userSelect: 'auto',
                      WebkitUserSelect: 'auto',
                      WebkitTouchCallout: 'default',
                      touchAction: 'manipulation',
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Dialog>
      <Snackbar
        open={copySnackbar.open}
        autoHideDuration={2500}
        onClose={() => setCopySnackbar({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={
            getPlatform() === 'ios'
              ? undefined
              : () => setCopySnackbar({ open: false, message: '' })
          }
          severity="success"
        >
          {copySnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PostCard;

